import * as XLSX from 'xlsx-js-style';
import { InvestmentTransactionDTO } from '@inversor-hub/types';
import {
  transactionCategoryLabels,
  transactionTypeLabels,
} from '@/features/investment-transactions/constants';
import {
  BUCKETS_BY_ID,
  BucketKind,
  FiscalAggregate,
  IRPF_BUCKETS,
  mapCategoryToBucket,
} from './fiscalCategories';

const DISCLAIMER =
  'Este resumen no es un cálculo fiscal. Organiza tus ingresos y gastos en las categorías que usa tu gestor para el IRPF. Faltan partidas que solo tu asesor puede calcular correctamente (como la amortización o la reducción por vivienda habitual). Úsalo para ahorrar tiempo en la declaración, no para sustituirla.';
const EURO_FORMAT = '#,##0.00 [$EUR]';
const COLORS = {
  navy: '1F3A5F',
  blue: 'DCE8F5',
  blueBorder: '9BB3CC',
  slate: '5C6B7A',
  lightSlate: 'F5F7FA',
  green: 'DFF3E4',
  red: 'FBE4E6',
  amber: 'FFF4D6',
  white: 'FFFFFF',
};

const baseBorder = {
  top: { style: 'thin', color: { rgb: COLORS.blueBorder } },
  bottom: { style: 'thin', color: { rgb: COLORS.blueBorder } },
  left: { style: 'thin', color: { rgb: COLORS.blueBorder } },
  right: { style: 'thin', color: { rgb: COLORS.blueBorder } },
};

const titleStyle = {
  font: { bold: true, color: { rgb: COLORS.white }, sz: 15 },
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.navy } },
  alignment: { horizontal: 'center', vertical: 'center' },
};

const subtitleStyle = {
  font: { bold: true, color: { rgb: COLORS.navy }, sz: 11 },
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.blue } },
  alignment: { horizontal: 'center', vertical: 'center' },
};

const sectionStyle = {
  font: { bold: true, color: { rgb: COLORS.navy }, sz: 11 },
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.blue } },
  alignment: { horizontal: 'left', vertical: 'center' },
  border: baseBorder,
};

const tableHeaderStyle = {
  font: { bold: true, color: { rgb: COLORS.white } },
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.slate } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: baseBorder,
};

const labelStyle = {
  font: { bold: true, color: { rgb: COLORS.navy } },
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.lightSlate } },
  alignment: { vertical: 'center' },
  border: baseBorder,
};

const valueStyle = {
  alignment: { vertical: 'center', wrapText: true },
  border: baseBorder,
};

const bodyStyle = {
  alignment: { vertical: 'top', wrapText: true },
  border: baseBorder,
};

const positiveValueStyle = {
  ...valueStyle,
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.green } },
  font: { bold: true, color: { rgb: '215E34' } },
};

const negativeValueStyle = {
  ...valueStyle,
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.red } },
  font: { bold: true, color: { rgb: 'A63A50' } },
};

const neutralValueStyle = {
  ...valueStyle,
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.blue } },
  font: { bold: true, color: { rgb: COLORS.navy } },
};

const warningTitleStyle = {
  font: { bold: true, color: { rgb: '8A5A00' } },
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.amber } },
  alignment: { vertical: 'center' },
  border: baseBorder,
};

const warningBodyStyle = {
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.amber } },
  alignment: { vertical: 'top', wrapText: true },
  border: baseBorder,
};

export interface FiscalExportContext {
  investmentName: string;
  investmentIdentificationCode?: string;
  investmentAddress: string;
  tenants: Array<{
    fullName: string;
    documentNumber: string;
    startDate: string;
    endDate?: string | null;
  }>;
  year: number;
  aggregate: FiscalAggregate;
  transactions: InvestmentTransactionDTO[];
}

function slugify(input: string): string {
  return (
    input
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'inmueble'
  );
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatDateForExport(iso: string): string {
  return iso.slice(0, 10).split('-').reverse().join('/');
}

function getBucketKindLabel(kind: BucketKind): string {
  switch (kind) {
    case 'income':
      return 'Ingresos';
    case 'deductible':
      return 'Gasto deducible';
    case 'non-deductible':
      return 'No deducible';
    case 'separate':
      return 'Revisar aparte';
    case 'uncategorized':
      return 'Revisar';
  }
}

function setCurrencyFormat(
  worksheet: XLSX.WorkSheet,
  columnIndex: number,
  startRowIndex: number,
  endRowIndex: number
) {
  for (let rowIndex = startRowIndex; rowIndex <= endRowIndex; rowIndex++) {
    const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
    if (worksheet[cellRef]) {
      worksheet[cellRef].z = EURO_FORMAT;
    }
  }
}

function ensureCell(worksheet: XLSX.WorkSheet, rowIndex: number, columnIndex: number) {
  const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
  if (!worksheet[cellRef]) {
    worksheet[cellRef] = { t: 's', v: '' };
  }
  return worksheet[cellRef];
}

function applyStyleToRange(
  worksheet: XLSX.WorkSheet,
  startRowIndex: number,
  endRowIndex: number,
  startColumnIndex: number,
  endColumnIndex: number,
  style: Record<string, unknown>
) {
  for (let rowIndex = startRowIndex; rowIndex <= endRowIndex; rowIndex++) {
    for (let columnIndex = startColumnIndex; columnIndex <= endColumnIndex; columnIndex++) {
      ensureCell(worksheet, rowIndex, columnIndex).s = style;
    }
  }
}

export function exportFiscalXLSX(ctx: FiscalExportContext) {
  const {
    investmentName,
    investmentIdentificationCode,
    investmentAddress,
    tenants,
    year,
    aggregate,
    transactions,
  } = ctx;
  const investmentExportLabel = investmentIdentificationCode
    ? `${investmentName} · ${investmentIdentificationCode}`
    : investmentName;

  const toEuros = (cents: number) => Number((cents / 100).toFixed(2));
  const rowsInYear = transactions
    .filter((tx) => new Date(tx.date).getUTCFullYear() === year)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date));
  const allocationRowsInYear = rowsInYear.flatMap((tx) => {
    const lines = tx.lines.length > 0 ? tx.lines : [{ category: tx.category, amount: tx.amount }];
    return lines.map((line) => ({
      ...tx,
      category: line.category,
      amount: line.amount,
    }));
  });
  const populatedBuckets = IRPF_BUCKETS.filter(
    (bucket) => aggregate.byBucket[bucket.id].transactions.length > 0
  );
  const totalIncomeInYear = rowsInYear
    .filter((tx) => tx.type === 'INCOME')
    .reduce((sum, tx) => sum + tx.amount, 0);
  const totalExpensesInYear = rowsInYear
    .filter((tx) => tx.type === 'EXPENSE')
    .reduce((sum, tx) => sum + tx.amount, 0);
  const netCashImpactInYear = totalIncomeInYear - totalExpensesInYear;

  const summaryRows: (string | number)[][] = [
    ['Resumen fiscal organizativo', '', '', '', ''],
    [`${investmentExportLabel} · ejercicio ${year}`, '', '', '', ''],
    [],
    ['Inmueble', investmentName, '', 'Ejercicio fiscal', year],
    ['Código de identificación', investmentIdentificationCode ?? '', '', '', ''],
    ['Dirección', investmentAddress, '', 'Transacciones incluidas', rowsInYear.length],
    ['Inquilinos registrados', tenants.length, '', '', ''],
    [],
    ['Indicadores clave', '', '', '', ''],
    ['Concepto', 'Importe (€)', 'Lectura', '', ''],
    [
      'Ingresos íntegros',
      toEuros(aggregate.totals.grossIncome),
      'Rentas e ingresos agrupados para IRPF',
      '',
      '',
    ],
    [
      'Gastos deducibles',
      toEuros(aggregate.totals.deductibleExpenses),
      'Partidas potencialmente deducibles que conviene revisar con gestor',
      '',
      '',
    ],
    [
      'Saldo organizativo',
      toEuros(aggregate.totals.organizationalBalance),
      'Diferencia entre ingresos íntegros y gastos deducibles. No equivale al rendimiento neto fiscal.',
      '',
      '',
    ],
    [],
    ['Desglose por categoría', '', '', '', ''],
    ['Bloque', 'Categoría IRPF', 'Detalle', 'Total (€)', 'Nº transacciones'],
  ];

  for (const bucket of populatedBuckets) {
    const entry = aggregate.byBucket[bucket.id];
    summaryRows.push([
      getBucketKindLabel(bucket.kind),
      bucket.label,
      bucket.subtitle,
      toEuros(entry.total),
      entry.transactions.length,
    ]);
  }

  summaryRows.push([]);
  summaryRows.push(['Aviso importante', '', '', '', '']);
  summaryRows.push([DISCLAIMER, '', '', '', '']);

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
  summarySheet['!cols'] = [{ wch: 22 }, { wch: 28 }, { wch: 58 }, { wch: 14 }, { wch: 16 }];
  summarySheet['!merges'] = [
    XLSX.utils.decode_range('A1:E1'),
    XLSX.utils.decode_range('A2:E2'),
    XLSX.utils.decode_range('A9:E9'),
    XLSX.utils.decode_range('A15:E15'),
    XLSX.utils.decode_range(`A${summaryRows.length - 1}:E${summaryRows.length - 1}`),
    XLSX.utils.decode_range(`A${summaryRows.length}:E${summaryRows.length}`),
  ];
  summarySheet['!rows'] = [{ hpt: 24 }, { hpt: 20 }];
  setCurrencyFormat(summarySheet, 1, 9, 11);
  setCurrencyFormat(summarySheet, 3, 16, 15 + populatedBuckets.length);
  applyStyleToRange(summarySheet, 0, 0, 0, 4, titleStyle);
  applyStyleToRange(summarySheet, 1, 1, 0, 4, subtitleStyle);
  applyStyleToRange(summarySheet, 3, 6, 0, 0, labelStyle);
  applyStyleToRange(summarySheet, 3, 6, 1, 1, valueStyle);
  applyStyleToRange(summarySheet, 3, 5, 3, 3, labelStyle);
  applyStyleToRange(summarySheet, 3, 5, 4, 4, valueStyle);
  applyStyleToRange(summarySheet, 8, 8, 0, 4, sectionStyle);
  applyStyleToRange(summarySheet, 9, 9, 0, 4, tableHeaderStyle);
  applyStyleToRange(summarySheet, 10, 10, 0, 0, labelStyle);
  applyStyleToRange(summarySheet, 11, 11, 0, 0, labelStyle);
  applyStyleToRange(summarySheet, 12, 12, 0, 0, labelStyle);
  applyStyleToRange(summarySheet, 10, 10, 1, 1, positiveValueStyle);
  applyStyleToRange(summarySheet, 11, 11, 1, 1, negativeValueStyle);
  applyStyleToRange(summarySheet, 12, 12, 1, 1, neutralValueStyle);
  applyStyleToRange(summarySheet, 10, 12, 2, 4, bodyStyle);
  applyStyleToRange(summarySheet, 14, 14, 0, 4, sectionStyle);
  applyStyleToRange(summarySheet, 15, 15, 0, 4, tableHeaderStyle);
  if (populatedBuckets.length > 0) {
    applyStyleToRange(summarySheet, 16, 15 + populatedBuckets.length, 0, 2, bodyStyle);
    applyStyleToRange(summarySheet, 16, 15 + populatedBuckets.length, 3, 4, valueStyle);
  }
  applyStyleToRange(
    summarySheet,
    summaryRows.length - 2,
    summaryRows.length - 2,
    0,
    4,
    warningTitleStyle
  );
  applyStyleToRange(
    summarySheet,
    summaryRows.length - 1,
    summaryRows.length - 1,
    0,
    4,
    warningBodyStyle
  );

  const txRows: (string | number)[][] = [
    ['Detalle de transacciones del ejercicio', '', '', '', '', '', '', '', ''],
    [
      `${investmentExportLabel} · ${year} · ${rowsInYear.length} movimientos`,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
    ],
    [],
    [
      'Fecha',
      'Tipo',
      'Concepto',
      'Código de identificación',
      'Categoría original',
      'Bloque fiscal',
      'Categoría IRPF',
      'Importe (€)',
      'Impacto caja (€)',
    ],
  ];

  for (const tx of allocationRowsInYear) {
    const bucket = BUCKETS_BY_ID[mapCategoryToBucket(tx.category)];
    const signedCashImpact = tx.type === 'INCOME' ? tx.amount : -tx.amount;

    txRows.push([
      formatDateForExport(tx.date),
      transactionTypeLabels[tx.type],
      tx.concept ?? 'Sin concepto',
      investmentIdentificationCode ?? '',
      transactionCategoryLabels[tx.category],
      getBucketKindLabel(bucket.kind),
      bucket.label,
      toEuros(tx.amount),
      toEuros(signedCashImpact),
    ]);
  }

  txRows.push([]);
  txRows.push(['', '', '', '', '', '', 'Total ingresos', toEuros(totalIncomeInYear), '']);
  txRows.push(['', '', '', '', '', '', 'Total gastos', toEuros(totalExpensesInYear), '']);
  txRows.push(['', '', '', '', '', '', 'Impacto neto de caja', '', toEuros(netCashImpactInYear)]);

  const txSheet = XLSX.utils.aoa_to_sheet(txRows);
  txSheet['!cols'] = [
    { wch: 12 },
    { wch: 12 },
    { wch: 28 },
    { wch: 24 },
    { wch: 22 },
    { wch: 18 },
    { wch: 32 },
    { wch: 14 },
    { wch: 16 },
  ];
  txSheet['!merges'] = [XLSX.utils.decode_range('A1:I1'), XLSX.utils.decode_range('A2:I2')];
  txSheet['!rows'] = [{ hpt: 24 }, { hpt: 20 }];
  txSheet['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 3, c: 0 },
      e: { r: Math.max(3, allocationRowsInYear.length + 3), c: 8 },
    }),
  };
  setCurrencyFormat(txSheet, 7, 4, allocationRowsInYear.length + 3);
  setCurrencyFormat(txSheet, 8, 4, allocationRowsInYear.length + 3);
  const totalsStartRow = allocationRowsInYear.length + 5;
  setCurrencyFormat(txSheet, 7, totalsStartRow, totalsStartRow + 1);
  setCurrencyFormat(txSheet, 8, totalsStartRow + 2, totalsStartRow + 2);
  applyStyleToRange(txSheet, 0, 0, 0, 8, titleStyle);
  applyStyleToRange(txSheet, 1, 1, 0, 8, subtitleStyle);
  applyStyleToRange(txSheet, 3, 3, 0, 8, tableHeaderStyle);
  if (allocationRowsInYear.length > 0) {
    applyStyleToRange(txSheet, 4, allocationRowsInYear.length + 3, 0, 6, bodyStyle);
    applyStyleToRange(txSheet, 4, allocationRowsInYear.length + 3, 7, 7, valueStyle);
    applyStyleToRange(txSheet, 4, allocationRowsInYear.length + 3, 8, 8, valueStyle);
  }
  applyStyleToRange(txSheet, totalsStartRow, totalsStartRow + 2, 6, 6, labelStyle);
  applyStyleToRange(txSheet, totalsStartRow, totalsStartRow + 1, 7, 7, valueStyle);
  applyStyleToRange(txSheet, totalsStartRow + 2, totalsStartRow + 2, 8, 8, neutralValueStyle);

  const tenantRows: (string | number)[][] = [
    ['Listado de inquilinos', '', '', '', '', ''],
    [`${investmentExportLabel} · datos asociados al inmueble`, '', '', '', '', ''],
    [],
    [
      'Inmueble',
      'Código de identificación',
      'Nombre completo',
      'DNI/NIE',
      'Fecha entrada',
      'Fecha salida',
    ],
  ];

  for (const tenant of tenants) {
    tenantRows.push([
      investmentName,
      investmentIdentificationCode ?? '',
      tenant.fullName,
      tenant.documentNumber,
      formatDateForExport(tenant.startDate),
      tenant.endDate ? formatDateForExport(tenant.endDate) : '',
    ]);
  }

  if (tenants.length === 0) {
    tenantRows.push([investmentName, investmentIdentificationCode ?? '', '', '', '', '']);
  }

  const tenantsSheet = XLSX.utils.aoa_to_sheet(tenantRows);
  tenantsSheet['!cols'] = [
    { wch: 28 },
    { wch: 24 },
    { wch: 32 },
    { wch: 18 },
    { wch: 14 },
    { wch: 14 },
  ];
  tenantsSheet['!merges'] = [XLSX.utils.decode_range('A1:F1'), XLSX.utils.decode_range('A2:F2')];
  tenantsSheet['!rows'] = [{ hpt: 24 }, { hpt: 20 }];
  tenantsSheet['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 3, c: 0 },
      e: { r: Math.max(4, tenantRows.length - 1), c: 5 },
    }),
  };
  applyStyleToRange(tenantsSheet, 0, 0, 0, 5, titleStyle);
  applyStyleToRange(tenantsSheet, 1, 1, 0, 5, subtitleStyle);
  applyStyleToRange(tenantsSheet, 3, 3, 0, 5, tableHeaderStyle);
  applyStyleToRange(tenantsSheet, 4, tenantRows.length - 1, 0, 5, bodyStyle);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen');
  XLSX.utils.book_append_sheet(workbook, txSheet, 'Transacciones');
  XLSX.utils.book_append_sheet(workbook, tenantsSheet, 'Inquilinos');

  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', cellStyles: true });
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `resumen-fiscal-${slugify(investmentName)}-${year}.xlsx`);
}
